import React, { useEffect, useState } from 'react';
import { fetchData, postData } from '../components/misc';

export default function MaintenanceToggle() {
    const [checked, setChecked] = useState(false);
    const [loading, setLoading] = useState(true);
    const [userEmail, setUserEmail] = useState('');
    const [userChecked, setUserChecked] = useState(false);
    const [users, setUsers] = useState([]);

    useEffect(() => {
        const controller = new AbortController();
        fetchData('/api/maintenance', { signal: controller.signal })
            .then((data) => {
                if (data && typeof data.maintenance !== 'undefined') {
                    setChecked(Boolean(data.maintenance));
                }
            })
            .catch((err) => console.error('Failed to load maintenance status', err))
            .finally(() => setLoading(false));
        return () => controller.abort();
    }, []);

    useEffect(() => {
        fetchData('/api/maintenance/users')
            .then((data) => {
                if (data && Array.isArray(data.users)) {
                    setUsers(data.users);
                }
            })
            .catch((err) => console.error('Failed to load users', err));
    }, []);

    useEffect(() => {
        if (!userEmail) return;
        fetchData(`/api/maintenance/user?email=${encodeURIComponent(userEmail)}`)
            .then((data) => {
                if (data && typeof data.maintenance !== 'undefined') {
                    setUserChecked(Boolean(data.maintenance));
                }
            })
            .catch((err) => console.error('Failed to load user maintenance', err));
    }, [userEmail]);

    const toggle = (e) => {
        const newState = e.target.checked;
        setChecked(newState);
        postData('/api/maintenance', { maintenance: newState })
            .catch((err) => console.error('Failed to update maintenance status', err));
    };

    const saveUser = () => {
        if (!userEmail) return;
        postData('/api/maintenance/user', { email: userEmail, maintenance: userChecked })
            .catch((err) => console.error('Failed to update user maintenance', err));
    };

    const updateUser = (email, maintenance) => {
        postData('/api/maintenance/user', { email, maintenance })
            .then(() => {
                setUsers((prev) => prev.map((u) => u.email === email ? { ...u, maintenance } : u));
            })
            .catch((err) => console.error('Failed to update user maintenance', err));
    };

    const muteNotifications = (email) => {
        postData('/api/maintenance/mute_notifications', { email })
            .then(() => {
                setUsers((prev) => prev.map((u) => u.email === email ? { ...u, mutednotifications: true } : u));
            })
            .catch((err) => console.error('Failed to mute notifications', err));
    };

    const unmuteNotifications = (email) => {
        postData('/api/maintenance/unmute_notifications', { email })
            .then(() => {
                setUsers((prev) => prev.map((u) => u.email === email ? { ...u, mutednotifications: false } : u));
            })
            .catch((err) => console.error('Failed to unmute notifications', err));
    };

    const toggleDenialOfService = (email, state) => {
        postData('/api/maintenance/denial_of_service', { email, denialofservice: state })
            .then(() => {
                setUsers((prev) => prev.map((u) => u.email === email ? { ...u, denialofservice: state } : u));
            })
            .catch((err) => console.error('Failed to update denial of service', err));
    };

    const toggleIndebt = (email, state) => {
        postData('/api/maintenance/indebt', { email, indebt: state })
            .then(() => {
                setUsers((prev) => prev.map((u) => u.email === email ? { ...u, indebt: state } : u));
            })
            .catch((err) => console.error('Failed to update indebt status', err));
    };

    const autoMessage = (email) => {
        const formdata = new FormData();
        formdata.append('EMAIL', email);

        const url = '/api/sirri_api/auto_messages_email';
        const config = {
            headers: {
                'content-type': 'multipart/form-data',
            },
        };

        postData(url, formdata, config)
            .then((data) => {
                if (data.responsecode === 200) {
                    alert(`Auto Messaged. Matches: ${data.matches.length}`);
                }
            })
            .catch((err) => console.error('Error sending auto message', err));
    };

    const blockAll = () => {
        postData('/api/maintenance/block_women', {})
            .then(() => {
                setUsers((prev) => prev.map((u) => ({ ...u, maintenance: true })));
            })
            .catch((err) => console.error('Failed to block all users', err));
    };

    if (loading) {
        return <p>Loading...</p>;
    }

    return (
        <div className="space-y-4">
            <label className="flex items-center space-x-2">
                <input type="checkbox" checked={checked} onChange={toggle} />
                <span className="text-white">Maintenance Mode</span>
            </label>
            <div className="flex items-center space-x-2">
                <input className="text-black p-1" placeholder="User email" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} />
                <input type="checkbox" checked={userChecked} onChange={(e) => setUserChecked(e.target.checked)} />
                <button className="bg-gray-700 text-white px-2 py-1" onClick={saveUser}>Save</button>
                <button className="bg-gray-700 text-white px-2 py-1" onClick={blockAll}>Block All</button>
            </div>
            <div className="space-y-2">
                {users.map((u) => (
                    <div key={u.email} className="flex items-center space-x-2">
                        <span className="text-white">{u.fullname1} ({u.email}) ({u.package1})</span>
                        <button
                            className="bg-gray-700 text-white px-2 py-1"
                            onClick={() => updateUser(u.email, !u.maintenance)}
                        >
                            {u.maintenance ? 'Disable' : 'Enable'}
                        </button>
                        <button
                            className="bg-gray-700 text-white px-2 py-1"
                            onClick={() => (u.mutednotifications ? unmuteNotifications(u.email) : muteNotifications(u.email))}
                        >
                            {u.mutednotifications ? 'Unmute notifications' : 'Mute notifications'}
                        </button>
                        <button
                            className="bg-gray-700 text-white px-2 py-1"
                            onClick={() => toggleDenialOfService(u.email, !u.denialofservice)}
                        >
                            {u.denialofservice ? 'Allow service' : 'Denial of Service'}
                        </button>
                        <button
                            className="bg-gray-700 text-white px-2 py-1"
                            onClick={() => toggleIndebt(u.email, !u.indebt)}
                        >
                            {u.indebt ? 'Clear Debt' : 'Set In Debt'}
                        </button>
                        <button
                            className="bg-gray-700 text-white px-2 py-1"
                            onClick={() => autoMessage(u.email)}
                        >
                            Auto Message
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}
